import React, { useRef, useEffect, useCallback } from 'react';
import { lerp, clamp, ease, damp, isMobile, isReduced } from '../hooks/useAnimation';
import '../styles/FStage.css';

// 卡片数据 - 完全匹配原 HTML 结构
const FCARDS = [
  {
    index: 0,
    frontTitle: '简单部署',
    frontTag: 'DEPLOY',
    frontPreview: 'discover',
    backTitle: '简单部署',
    backTag: '01 · DEPLOY',
    backRows: ['无需注册域名', '无需备案与配置服务器', '无需先注册微信账号', 'HTML 发布后即可公网访问'],
  },
  {
    index: 1,
    frontTitle: '立即运行',
    frontTag: 'RUN NOW',
    frontPreview: 'run',
    backTitle: '立即运行',
    backTag: '02 · RUN',
    backRows: ['网页先直接预览效果', '在 workbuddy 中运行完整程序', '在 Codex 中继续修改', '在 Claude Code 中接着开发'],
  },
  {
    index: 2,
    frontTitle: '基于开发平台的 APP',
    frontTag: '1/10 COST',
    frontPreview: 'detail',
    backTitle: '基于开发平台的 APP',
    backTag: '03 · PLATFORM',
    backRows: ['基于成熟开发平台完成应用', '数据、Skill 与视觉呈现协同生成', '复用现有能力，不必从零开发', '开发成本降到原来的 1/10'],
  },
  {
    index: 3,
    frontTitle: '人人可编程',
    frontTag: 'ZERO TO APP',
    frontPreview: 'package',
    backTitle: '人人可编程',
    backTag: '04 · CREATE',
    backRows: ['不要求代码基础', '用自然语言表达要解决的问题', '数据、Skill 与界面逐步生成', '随时做出自己的应用'],
  },
  {
    index: 4,
    frontTitle: '最快的内部沟通',
    frontTag: 'ALIGN',
    frontPreview: 'creator',
    backTitle: '最快的内部沟通',
    backTag: '05 · ALIGN',
    backRows: ['为上游做一个理解需求的应用', '为下游做一个接住结果的应用', '复杂信息被结构化、穿透表达', '让上下游真正理解彼此'],
  },
  {
    index: 5,
    frontTitle: '最懂你的客户',
    frontTag: 'CUSTOMER',
    frontPreview: 'get',
    backTitle: '最懂你的客户',
    backTag: '06 · CUSTOMER',
    backRows: ['在开发平台内持续沟通', '把客户背景和需求留在上下文', '随时生成定制解决方案', '成为最懂客户的人'],
  },
];

// 叠态参数 - 完全匹配原 HTML
const F_ROT = [-7, -4.2, -1.4, 1.4, 4.2, 7];
const F_OX = [-13, -8, -3, 3, 8, 13];
const F_OY = [11, 6, 1, -4, -9, -13];

// 预览组件
function P3Preview({ type }) {
  switch (type) {
    case 'discover':
      return (
        <div className="p3-preview p3-discover">
          <div className="p3-preview-bar">
            <i></i><i></i><i></i>
            <span>nuwork.cn / deploy</span>
          </div>
          <div className="p3-search-box">HTML 一键发布<em>LIVE</em></div>
          <div className="p3-tags">
            <i>无需域名</i>
            <i>无需备案</i>
            <i>无需服务器</i>
          </div>
          <div className="p3-apps"><i></i><i></i><i></i></div>
        </div>
      );
    case 'run':
      return (
        <div className="p3-preview p3-run">
          <div className="p3-preview-bar">
            <i></i><i></i><i></i>
            <span>live preview / full app</span>
          </div>
          <div className="p3-run-stage">
            <span className="p3-play"></span>
          </div>
        </div>
      );
    case 'detail':
      return (
        <div className="p3-preview p3-detail">
          <div className="p3-preview-bar">
            <i></i><i></i><i></i>
            <span>workbuddy / codex / claude code</span>
          </div>
          <div className="p3-detail-main">
            <b>开发平台生成 APP</b>
            <i></i><i></i>
            <em>成本 1/10</em>
          </div>
          <div className="p3-detail-side">
            <i>数据</i>
            <i>Skill</i>
            <i>视觉</i>
          </div>
        </div>
      );
    case 'package':
      return (
        <div className="p3-preview p3-package">
          <div className="p3-preview-bar">
            <i></i><i></i><i></i>
            <span>everyone can build</span>
          </div>
          <div className="p3-packages">
            <i>IDEA</i>
            <i>AI</i>
            <i>APP</i>
          </div>
          <div className="p3-package-ready">你的应用已就绪<i></i></div>
        </div>
      );
    case 'creator':
      return (
        <div className="p3-preview p3-creator">
          <div className="p3-preview-bar">
            <i></i><i></i><i></i>
            <span>internal app collaboration</span>
          </div>
          <div className="p3-creator-head">
            <i className="p3-avatar">UP</i>
            <span>
              <b>上下游应用协同</b>
              <small>INFORMATION ALIGNED</small>
            </span>
            <em className="p3-creator-stat">CLEAR</em>
          </div>
          <div className="p3-creator-apps"><i></i><i></i><i></i></div>
        </div>
      );
    case 'get':
      return (
        <div className="p3-preview p3-get">
          <div className="p3-preview-bar">
            <i></i><i></i><i></i>
            <span>customer.solution / live</span>
          </div>
          <div className="p3-get-link">客户需求已理解<em>生成方案</em></div>
          <div className="p3-transfer">
            <span>持续沟通</span>
            <i></i>
            <span>定制方案</span>
          </div>
        </div>
      );
    default:
      return null;
  }
}

function FStage() {
  const sectionRef = useRef(null);
  const cardsRef = useRef([]);
  const innersRef = useRef([]);

  // 动画状态
  const animRef = useRef({
    fpS: 0,
    frameRunning: false,
    lastFrameTime: performance.now(),
    M: { modules: { top: 0, h: 0 }, fch: 0 },
  });

  // 测量尺寸
  const measure = useCallback(() => {
    const el = sectionRef.current;
    if (!el) return;

    const rect = el.getBoundingClientRect();
    animRef.current.M = {
      modules: { top: rect.top + window.scrollY, h: el.offsetHeight },
      fch: cardsRef.current[0]?.offsetHeight || 306,
    };
  }, []);

  // 动画帧
  const frame = useCallback((now) => {
    const anim = animRef.current;
    const { M } = anim;
    const vh = window.innerHeight;
    const y = window.scrollY;
    const motion = !isMobile() && !isReduced();

    const dt = Math.min(0.05, Math.max(0.001, (now - anim.lastFrameTime) / 1000));
    anim.lastFrameTime = now;

    let needsFrame = false;

    // 计算滚动进度
    const fTop = M.modules.top - y;
    let fp = null;
    if (fTop < vh && fTop + M.modules.h > 0) {
      fp = clamp(-fTop / Math.max(M.modules.h - vh, 1), 0, 1);
    }

    if (!motion) {
      // 非动画模式：直接显示散开翻转状态
      const gw = Math.min(370, (window.innerWidth - 100) / 3);
      const gh = M.fch + 24;

      cardsRef.current.forEach((c, i) => {
        if (!c) return;
        const col = (i % 3) - 1;
        const row = i < 3 ? -0.5 : 0.5;
        c.style.opacity = 1;
        c.style.transform = `translate(${col * gw}px, ${row * gh}px) rotate(0deg) scale(1)`;
        c.style.zIndex = 10 + i;
        if (innersRef.current[i]) {
          innersRef.current[i].style.transform = 'rotateY(180deg)';
        }
      });
    } else if (fp !== null) {
      // 平滑过渡
      anim.fpS = damp(anim.fpS, fp, 7.7, dt);
      if (Math.abs(fp - anim.fpS) < 0.001) anim.fpS = fp;
      else needsFrame = true;

      const f = anim.fpS;
      const fch = M.fch;

      // 叠翻散动画：落下(0-.14) → 整齐扇叠 → 整叠翻面(.3-.58) → 散成 3×2(.64-.96)
      const drop = ease(clamp(f / 0.14, 0, 1));
      const flip = ease(clamp((f - 0.3) / 0.28, 0, 1));
      const sp = ease(clamp((f - 0.64) / 0.32, 0, 1));

      const gw = Math.min(370, (window.innerWidth - 100) / 3);
      const gh = fch + 24;

      cardsRef.current.forEach((c, i) => {
        if (!c) return;
        const col = (i % 3) - 1;
        const row = i < 3 ? -0.5 : 0.5;

        const x = lerp(F_OX[i], col * gw, sp);
        const yy = lerp(F_OY[i] - (1 - drop) * 80, row * gh, sp);
        const rot = lerp(F_ROT[i], 0, sp);
        const scl = lerp(1 - i * 0.012, 1, sp);

        c.style.opacity = drop;
        c.style.transform = `translate(${x}px, ${yy}px) rotate(${rot}deg) scale(${scl})`;
        c.style.zIndex = flip > 0.5 ? 10 + i : 16 - i;

        if (innersRef.current[i]) {
          innersRef.current[i].style.transform = `rotateY(${flip * 180}deg)`;
        }
      });
    }

    if (motion && needsFrame && !document.hidden) {
      requestAnimationFrame(frame);
    } else {
      anim.frameRunning = false;
    }
  }, []);

  // 启动动画帧
  const startFrame = useCallback(() => {
    const anim = animRef.current;
    if (anim.frameRunning) return;
    anim.frameRunning = true;
    anim.lastFrameTime = performance.now();
    requestAnimationFrame(frame);
  }, [frame]);

  // 初始化
  useEffect(() => {
    measure();
    startFrame();

    const handleScroll = () => startFrame();
    const handleResize = () => {
      measure();
      startFrame();
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, [measure, startFrame]);

  return (
    <section
      className="fstage-sec"
      id="modules"
      ref={sectionRef}
      aria-labelledby="modulesTitle"
    >
      <div className="fstage-sticky">
        <div className="fstage-head">
          <div className="p3-kicker">SOLVE THE HARD PARTS · 06</div>
          <h2 id="modulesTitle">
            解决当前困难，<br />
            <em>让每个人都能做应用。</em>
          </h2>
          <p>从部署、运行到内部沟通和客户服务——把做 APP 的门槛，一次降到底。</p>
          <span className="p3-mobile-hint">向下浏览 · 查看六个解决方案</span>
        </div>

        <div className="fstage" id="fstage" role="list" aria-label="解决做应用困难的六个方案">
          {FCARDS.map((card, i) => (
            <div
              key={i}
              className="fcard"
              data-fi={i}
              role="listitem"
              ref={(el) => (cardsRef.current[i] = el)}
            >
              <div
                className="fcard-inner"
                ref={(el) => (innersRef.current[i] = el)}
              >
                {/* 卡片正面 */}
                <div className="fcard-front" aria-hidden="true">
                  <div className="ff-art">
                    <P3Preview type={card.frontPreview} />
                  </div>
                  <div className="ff-name">
                    <b>{card.frontTitle}</b>
                    <span>{card.frontTag}</span>
                  </div>
                </div>

                {/* 卡片背面 */}
                <div className="fcard-back">
                  <div className="fb-head">
                    <b>{card.backTitle}</b>
                    <span className="mod-n">{card.backTag}</span>
                  </div>
                  <div className="fb-rows">
                    {card.backRows.map((row, j) => (
                      <i key={j}>{row}</i>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default FStage;
